import { DealCard } from "~/components/common/DealCard";
import { AdSlot } from "~/components/common/AdSlot";
import { AD_SLOTS } from "~/lib/ads";
import { DealGridSkeleton } from "~/components/skeleton/DealGridSkeleton";
import type { DealClientListData } from "~/types/deal";
import { Button } from "~/components/ui/button";
import { ChevronDown } from "lucide-react";
import { cn } from "~/lib/utils";
import { useUserStore } from "~/lib/stores/user";
import { AnalyticsEventBuilder } from "~/lib/analytics";
import { saveAnalyticsContext } from "~/lib/analytics-context";
import { trackDealLoadMore } from "~/lib/track-events";

interface DealPerRowProps {
  default: number;
  mobile: number;
  tablet: number;
  desktopSmall: number;
  desktopLarge: number;
}

// helper function to get the grid class name for Tailwind JIT
function getGridClassName(dealPerRow: DealPerRowProps): string {
  const gridColsMap = {
    1: "grid-cols-1",
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-4",
    5: "grid-cols-5",
    6: "grid-cols-6",
    7: "grid-cols-7",
    8: "grid-cols-8",
  } as const;

  const mobileGridColsMap = {
    1: "mobile:grid-cols-1",
    2: "mobile:grid-cols-2",
    3: "mobile:grid-cols-3",
    4: "mobile:grid-cols-4",
    5: "mobile:grid-cols-5",
    6: "mobile:grid-cols-6",
    7: "mobile:grid-cols-7",
    8: "mobile:grid-cols-8",
  } as const;

  const tabletGridColsMap = {
    1: "tablet:grid-cols-1",
    2: "tablet:grid-cols-2",
    3: "tablet:grid-cols-3",
    4: "tablet:grid-cols-4",
    5: "tablet:grid-cols-5",
    6: "tablet:grid-cols-6",
    7: "tablet:grid-cols-7",
    8: "tablet:grid-cols-8",
  } as const;

  const desktopSmallGridColsMap = {
    1: "desktop-small:grid-cols-1",
    2: "desktop-small:grid-cols-2",
    3: "desktop-small:grid-cols-3",
    4: "desktop-small:grid-cols-4",
    5: "desktop-small:grid-cols-5",
    6: "desktop-small:grid-cols-6",
    7: "desktop-small:grid-cols-7",
    8: "desktop-small:grid-cols-8",
  } as const;

  const desktopLargeGridColsMap = {
    1: "desktop-large:grid-cols-1",
    2: "desktop-large:grid-cols-2",
    3: "desktop-large:grid-cols-3",
    4: "desktop-large:grid-cols-4",
    5: "desktop-large:grid-cols-5",
    6: "desktop-large:grid-cols-6",
    7: "desktop-large:grid-cols-7",
    8: "desktop-large:grid-cols-8",
  } as const;

  return cn(
    "grid",
    "gap-4",
    gridColsMap[dealPerRow.default as keyof typeof gridColsMap],
    mobileGridColsMap[dealPerRow.mobile as keyof typeof mobileGridColsMap],
    tabletGridColsMap[dealPerRow.tablet as keyof typeof tabletGridColsMap],
    desktopSmallGridColsMap[dealPerRow.desktopSmall as keyof typeof desktopSmallGridColsMap],
    desktopLargeGridColsMap[dealPerRow.desktopLarge as keyof typeof desktopLargeGridColsMap]
  );
}

interface DealFeedProps {
  deals: DealClientListData[];
  onDealClick?: (deal: DealClientListData) => void;
  onLoadMore?: () => void;
  getBadgeText?: (deal: DealClientListData, index: number) => string | undefined;
  isLoading: boolean;
  hasMore: boolean;
  showNoMore?: boolean;
  className?: string;
  dealPerRow?: DealPerRowProps;
  channel?: string;
  /** Opt-in: insert one AdSlot cell after every N deals. Omit to show no ads (default for reuse). */
  adInterval?: number;
}

export function DealFeed({
  deals,
  onDealClick,
  onLoadMore,
  getBadgeText,
  isLoading = false,
  hasMore = false,
  showNoMore = true,
  className = "",
  dealPerRow = {
    default: 2,
    mobile: 2,
    tablet: 4,
    desktopSmall: 4,
    desktopLarge: 4,
  },
  channel,
  adInterval,
}: DealFeedProps) {
  const { user } = useUserStore();

  const handleDealClick = async (deal: DealClientListData, index: number) => {
    if (onDealClick) {
      onDealClick(deal);
    } else {
      // Save analytics context before navigation
      if (user && channel) {
        const contextAttrs = AnalyticsEventBuilder.createContextAttrs(channel, index);
        const itemAttrs = AnalyticsEventBuilder.createItemAttrs({
          currency: user.currency || "USD",
          price_dash: deal.price || 0,
        });

        await saveAnalyticsContext(deal._id, deal._productId, deal.reqId || "", contextAttrs, itemAttrs);
      }

      // Build URL with reqId if available
      let url = `/deal/detail?deal_id=${deal._id}`;
      if (deal.reqId) {
        url += `&req_id=${deal.reqId}`;
      }
      // open in new tab
      window.open(url, "_blank");
    }
  };

  // Implement manual load more button
  const handleLoadMore = () => {
    if (onLoadMore) {
      trackDealLoadMore({
        channel,
        loaded_count: deals.length,
        has_more: hasMore,
      });
      onLoadMore();
    }
  };

  const gridClassName = getGridClassName(dealPerRow);

  return (
    <div className={className}>
      {/* Show skeleton on initial loading when no deals */}
      {isLoading && deals.length === 0 ? (
        <DealGridSkeleton gridClassName={gridClassName} />
      ) : (
        <>
          {/* Deal Grid */}
          <div className={gridClassName}>
            {deals.flatMap((deal, index) => {
              const badgeText = getBadgeText ? getBadgeText(deal, index) : undefined;
              const card = (
                <DealCard
                  key={`${deal._id}-${index}`}
                  deal={deal}
                  badgeText={badgeText}
                  channel={channel}
                  position={index}
                  onClick={() => handleDealClick(deal, index)}
                />
              );
              // Insert an ad cell after every Nth deal. Position depends only on the index (not on
              // deals.length) so ads don't shift when "View more" appends deals. The ad is a single
              // grid cell like a deal card; a responsive display unit has no min width, so it fits
              // the ~195px desktop cell and the ~160px mobile cell alike.
              if (adInterval && (index + 1) % adInterval === 0) {
                const adNumber = (index + 1) / adInterval;
                return [
                  card,
                  <AdSlot
                    key={`ad-${adNumber}`}
                    id={`deal-feed-ad-${adNumber}`}
                    placementId="deal.feed.display"
                    slot={AD_SLOTS.dealFeed.slot}
                    format={AD_SLOTS.dealFeed.format}
                    fullWidthResponsive={AD_SLOTS.dealFeed.fullWidthResponsive}
                  />,
                ];
              }
              return [card];
            })}
          </div>
        </>
      )}

      {/* Load more button */}
      {onLoadMore && hasMore && !isLoading && (
        <div className="mt-6 flex justify-center">
          <Button id="deal-feed-load-more-button" variant="outline" onClick={handleLoadMore}>
            View more <ChevronDown className="ml-2" />
          </Button>
        </div>
      )}

      {/* Loading more deals skeleton */}
      {isLoading && deals.length > 0 && (
        <div className="mt-6">
          <DealGridSkeleton count={4} gridClassName={gridClassName} />
        </div>
      )}

      {/* End of deals indicator */}
      {!hasMore && deals.length > 0 && showNoMore && (
        <div className="mt-6 flex justify-center">
          <div className="text-gray-500">No more deals to load</div>
        </div>
      )}
    </div>
  );
}
